import React from 'react';
import { View, Text } from 'react-native';
import { useThemeColors } from '@/lib/useThemeColors';

interface MiniBarChartProps {
  data: { label: string; value: number }[];
  color?: string;
  height?: number;
  showLabels?: boolean;
}

/**
 * Axis labels must FIT. Each label gets 1/7th of the screen width at 9px with
 * numberOfLines={1}, so a full ISO date renders as "2026-08…" — seven identical
 * useless stubs. The waitlist feed already emits "MM/DD"; the signup trend emits
 * ISO. Normalising here means no call site has to remember which is which.
 */
function shortAxisLabel(label: string): string {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(label);
  return iso ? `${iso[2]}/${iso[3]}` : label;
}

export function MiniBarChart({
  data,
  color,
  height = 100,
  showLabels = true,
}: MiniBarChartProps) {
  const colors = useThemeColors();
  const barColor = color ?? colors.primary[600];
  const maxVal = Math.max(...data.map((d) => d.value), 1);

  const hasAnyValue = data.some((d) => (d.value ?? 0) > 0);
  if (data.length === 0 || !hasAnyValue) {
    return (
      <View style={{ height }} className="items-center justify-center">
        <Text style={{ color: colors.text.tertiary }} className="text-xs">
          No data yet
        </Text>
      </View>
    );
  }

  return (
    <View style={{ height: height + (showLabels ? 20 : 0) }}>
      <View className="flex-row items-end justify-between flex-1 gap-1" style={{ height }}>
        {data.map((d, i) => {
          const barH = maxVal > 0 ? (d.value / maxVal) * height * 0.85 : 0;
          const finalH = Math.max(barH, 3);

          return (
            <View key={`bar-${i}`} className="flex-1 items-center justify-end" style={{ height }}>
              <View
                style={{
                  height: finalH,
                  backgroundColor: barColor,
                  borderRadius: 4,
                  width: '100%',
                  minWidth: 4,
                }}
              />
            </View>
          );
        })}
      </View>
      {showLabels && (
        <View className="flex-row justify-between mt-1">
          {data.map((d, i) => (
            <Text
              key={`label-${i}`}
              style={{ color: colors.text.tertiary }}
              className="text-[9px] text-center flex-1"
              numberOfLines={1}
            >
              {shortAxisLabel(d.label)}
            </Text>
          ))}
        </View>
      )}
    </View>
  );
}
